import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft, Search, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { useLanguage } from "@/contexts/LanguageContext";

const NotFound = () => {
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const popularPages = [
    { name: "Início", path: "/" },
    { name: "Blog", path: "/blog" },
    { name: "Sobre", path: "/about" },
    { name: "Portal do Cliente", path: "/portal" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title="Página não encontrada | Quimical"
        description="A página solicitada não foi encontrada. Acesse o início ou navegue para conteúdos e contato da Quimical."
        canonical="https://quimical.com.br/404"
      />
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Hero */}
            <div className="mb-16">
              <div className="mb-8">
                <h1 className="text-8xl md:text-9xl font-bold text-primary/20 mb-4">
                  404
                </h1>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Página não encontrada
                </h2>
                <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Desculpe, mas a página que você está procurando não existe ou foi movida. 
                  Verifique o endereço digitado ou navegue através dos links abaixo.
                </p>
              </div>

              {/* URL Info */}
              <Card className="mb-8 max-w-2xl mx-auto">
                <CardContent className="p-6">
                  <div className="flex items-center justify-center text-sm text-muted-foreground">
                    <Search className="w-4 h-4 mr-2" />
                    <span className="font-mono bg-muted px-2 py-1 rounded">
                      {typeof window !== "undefined" && window.location.origin}{location.pathname}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button asChild className="bg-primary hover:bg-primary/90">
                  <Link to="/">
                    <Home className="w-4 h-4 mr-2" />
                    Voltar ao Início
                  </Link>
                </Button>
                
                <Button variant="outline" onClick={() => window.history.back()}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Página Anterior
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => window.open("https://wa.me/5537998606150", "_blank")}
                  aria-label="Entrar em contato via WhatsApp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Fale Conosco
                </Button>
              </div>
            </div>

            {/* Popular Pages */}
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl font-semibold text-foreground mb-6">
                Páginas populares
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                {popularPages.map((page, index) => (
                  <Card key={index} className="hover:shadow-medium transition-shadow">
                    <CardContent className="p-6">
                      <Link 
                        to={page.path}
                        className="block text-left hover:text-primary transition-colors"
                      >
                        <h4 className="font-semibold text-foreground mb-2">
                          {page.name}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {page.path}
                        </p>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Help Section */}
            <div className="mt-16 p-8 bg-muted/30 rounded-lg">
              <h3 className="text-xl font-semibold text-foreground mb-4">
                Precisa de ajuda?
              </h3>
              <p className="text-muted-foreground mb-6">
                Nossa equipe está sempre disponível para ajudá-lo a encontrar o que precisa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  onClick={() => window.open("https://wa.me/5537998606150", "_blank")}
                  aria-label="Entrar em contato via WhatsApp"
                >
                  Entre em Contato
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open("https://wa.me/5537998606150", "_blank")}
                  aria-label="Entrar em contato via WhatsApp"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp: (37) 99860-6150
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
